import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { features, featureSlugs, type FeatureSlug } from "@/data/features";
import { SoftwareAppJsonLd } from "@/components/JsonLd";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const FeaturePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  if (!slug || !featureSlugs.includes(slug as FeatureSlug)) return <Navigate to="/" replace />;

  const feature = features[slug as FeatureSlug];
  const s = `features.${slug}`;

  const highlights = t(`${s}.highlights`, { returnObjects: true }) as string[];
  const capabilities = t(`${s}.capabilities`, { returnObjects: true }) as { title: string; description: string }[];

  const HeroIcon = feature.heroIcon;

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title={`${t(`${s}.label`)} — rawResponse {Labs}`}
        description={t(`${s}.description`)}
      />
      <SoftwareAppJsonLd name={t(`${s}.label`)} description={t(`${s}.description`)} slug={slug} />
      <Navbar />
      <main>


      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] radial-spot pointer-events-none opacity-40" />
        <div className="container max-w-5xl relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <HeroIcon size={20} className="text-primary" />
            </div>
            <p className="text-[11px] font-mono text-primary uppercase tracking-widest">
              {t(`${s}.label`)}
            </p>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="text-[clamp(2rem,5vw,3.25rem)] font-semibold tracking-tight leading-[1.08] mb-6"
          >
            {t(`${s}.headline`)}
            <br />
            <span className="text-muted-foreground">{t(`${s}.headlineMuted`)}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-[15px] font-mono text-muted-foreground leading-relaxed max-w-lg mb-10"
          >
            {t(`${s}.description`)}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex gap-3"
          >
            <a
              href="https://webblitz.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-[13px] font-mono font-medium hover:bg-primary/90 transition-colors"
            >
              {t("solutionPage.tryWebblitz")}
              <ArrowRight size={13} />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-border text-[13px] font-mono text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
            >
              {t("solutionPage.talkToUs")}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="container max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              custom={0}
              variants={fadeUp}
            >
              <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">
                {t(`${s}.whyLabel`)}
              </p>
              <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15] mb-4">
                {t(`${s}.whyHeadline`)}
              </h2>
              <p className="text-[14px] font-mono text-muted-foreground leading-relaxed">
                {t(`${s}.whyDescription`)}
              </p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              custom={1}
              variants={fadeUp}
              className="space-y-4 pt-2"
            >
              {highlights.map((point) => (
                <div key={point} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" />
                  <p className="text-[13px] font-mono text-muted-foreground leading-relaxed">
                    {point}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="container max-w-5xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={0}
            variants={fadeUp}
            className="max-w-2xl mb-16"
          >
            <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">
              {t(`${s}.capLabel`)}
            </p>
            <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15]">
              {t(`${s}.capHeadline`)}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            {capabilities.map((cap, i) => {
              const CapIcon = feature.capabilities[i]?.icon;
              return (
                <motion.div
                  key={cap.title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  custom={i * 0.5}
                  variants={fadeUp}
                  className="rounded-xl border border-border bg-card p-6"
                >
                  {CapIcon && (
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                      <CapIcon size={15} className="text-primary" />
                    </div>
                  )}
                  <h3 className="text-[14px] font-mono font-medium mb-2">{cap.title}</h3>
                  <p className="text-[12px] font-mono text-muted-foreground leading-relaxed">
                    {cap.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="container max-w-5xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={0}
            variants={fadeUp}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15] mb-4">
              {t(`${s}.ctaHeadline`)}
            </h2>
            <p className="text-[14px] font-mono text-muted-foreground leading-relaxed mb-8">
              {t(`${s}.ctaDescription`)}
            </p>
            <div className="flex items-center justify-center gap-3">
              <a
                href="https://webblitz.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-5 rounded-md bg-primary text-primary-foreground text-[13px] font-mono font-medium hover:bg-primary/90 transition-colors"
              >
                {t("solutionPage.tryWebblitz")}
                <ArrowRight size={13} />
              </a>
              <a
                href="/contact"
                className="inline-flex items-center gap-1.5 h-9 px-5 rounded-md border border-border text-[13px] font-mono text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
              >
                {t("solutionPage.talkToUs")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default FeaturePage;
